import React from 'react';
import { Check } from 'lucide-react';
import { typography } from '../../styles/typography';
import ScopeField, {
  BODY_TEXT_CLASS,
  SUBSECTION_TITLE_CLASS,
  titleCaseLabel,
} from './ScopeField';
import {
  filterVisibleFields,
  getHiddenFieldClears,
} from './scopeFieldVisibility';
import {
  getProgramFieldsBySection,
  getResolvedFieldsForRoomTemplate,
} from './scopeTemplateEngine';
import { emptyTipTapNotes } from './tipTapEmpty';
import TipTapScopeNotes from './TipTapScopeNotes';

const noop = () => {};

const SubsectionCard = ({ title, children }) => (
  <div className="flex flex-col gap-4 rounded-xl border border-white/60 bg-white/55 p-4 sm:p-5">
    <h5
      className={`${SUBSECTION_TITLE_CLASS} border-b border-zinc-200/80 pb-2 mb-0`}
    >
      {titleCaseLabel(title)}
    </h5>
    {children}
  </div>
);

const FieldsGrid = ({ fields, values, onPatch, readOnly }) => {
  const visibleFields = filterVisibleFields(fields, values);
  const notesField = visibleFields.find((f) => f.type === 'richNotes');
  const otherFields = visibleFields.filter((f) => f.type !== 'richNotes');

  const handleChange = (fieldId, value) => {
    if (readOnly) return;
    const nextValues = { ...values, [fieldId]: value };
    const clears = getHiddenFieldClears(fields, values, nextValues);
    const patch = { [fieldId]: value };
    clears.forEach((c) => {
      patch[c.fieldId] = c.value;
    });
    onPatch(patch);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {otherFields.map((field) => (
          <div
            key={field.id}
            className={
              field.type === 'checkboxGroup' ? 'md:col-span-2' : undefined
            }
          >
            <ScopeField
              field={field}
              value={values?.[field.id]}
              readOnly={readOnly}
              onChange={(next) => handleChange(field.id, next)}
            />
          </div>
        ))}
      </div>
      {notesField && (
        <ScopeField
          field={notesField}
          value={values?.[notesField.id]}
          readOnly={readOnly}
          onChange={(next) => handleChange(notesField.id, next)}
        />
      )}
    </div>
  );
};

const SectionHeader = ({ letter, title, hint }) => (
  <div>
    <h3 className={typography.h3} style={{ color: '#000000' }}>
      {letter} — {title}
    </h3>
    {hint ? <p className={`${BODY_TEXT_CLASS} mt-2`}>{hint}</p> : null}
  </div>
);

const ProgramFieldsBlock = ({ fields, program, onProgramPatch, readOnly }) => {
  if (!fields?.length) return null;
  return (
    <SubsectionCard title="Program">
      <FieldsGrid
        fields={fields}
        values={program}
        readOnly={readOnly}
        onPatch={onProgramPatch}
      />
    </SubsectionCard>
  );
};

/** Left nav for template wizard steps — PDF lettered sections */
export const TemplateScopeNav = ({
  steps = [],
  activeStepId,
  completedStepIds,
  onSelect,
  className = '',
}) => {
  const completedSet =
    completedStepIds instanceof Set
      ? completedStepIds
      : new Set(completedStepIds || []);

  return (
    <div className={`flex flex-col gap-1.5 overflow-y-auto pr-1 ${className}`}>
      {steps.map((step) => {
        const isActive = step.id === activeStepId;
        const isDone = completedSet.has(step.id);
        return (
          <button
            key={step.id}
            type="button"
            onClick={() => onSelect(step.id)}
            className={`w-full text-left rounded-xl px-3 py-2.5 border transition-colors ${
              isActive
                ? 'bg-white border-[#6BABA0] shadow-sm'
                : 'bg-white/45 border-transparent hover:bg-white/70'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <p className="flex-1 min-w-0 text-sm font-medium text-black font-['Open_Sans'] truncate">
                {step.letter} — {step.title}
              </p>
              {isDone ? (
                <span
                  className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"
                  title="Section completed"
                  aria-label="Section completed"
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
};

/**
 * Renders one wizard step from the ultimate-scope template + live scope document.
 * Step letters/titles follow the Ultimate Scope Sheet PDF (A–Q).
 */
const TemplateScopeForm = ({
  template,
  step,
  scopeDoc,
  onProgramPatch = noop,
  onAreaAnswersPatch = noop,
  onRoomAnswersPatch = noop,
  onDocumentNotesChange = noop,
  onRoomLabelChange = noop,
  readOnly = false,
}) => {
  if (!template || !step) return null;

  const program = scopeDoc?.program || {};

  if (step.kind === 'program') {
    const fields =
      step.programFields ||
      getProgramFieldsBySection(template, step.sectionLetter)[0]?.fields ||
      [];
    return (
      <div className="flex flex-col gap-6">
        <SectionHeader
          letter={step.letter}
          title={step.title}
          hint={
            step.letter === 'A'
              ? 'Set bedroom / help / store counts here. Balcony count is under section N.'
              : undefined
          }
        />
        <SubsectionCard title={step.title}>
          <FieldsGrid
            fields={fields}
            values={program}
            readOnly={readOnly}
            onPatch={onProgramPatch}
          />
        </SubsectionCard>
      </div>
    );
  }

  if (step.kind === 'document_notes') {
    const notes =
      scopeDoc?.documentNotes ||
      scopeDoc?.raw?.document_notes ||
      emptyTipTapNotes();
    return (
      <div className="flex flex-col gap-6">
        <SectionHeader letter={step.letter} title={step.title} />
        <SubsectionCard title={step.title}>
          <TipTapScopeNotes
            label=""
            value={notes}
            readOnly={readOnly}
            minHeight={220}
            onChange={onDocumentNotesChange}
          />
        </SubsectionCard>
      </div>
    );
  }

  if (step.kind === 'area') {
    const area = scopeDoc?.areas?.[step.roomType] || {};
    const groups = getResolvedFieldsForRoomTemplate(
      step.roomTemplate,
      template
    );
    return (
      <div className="flex flex-col gap-6">
        <SectionHeader letter={step.letter} title={step.title} />
        <ProgramFieldsBlock
          fields={step.programFields}
          program={program}
          onProgramPatch={onProgramPatch}
          readOnly={readOnly}
        />
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <SubsectionCard key={group.id} title={group.title}>
              <FieldsGrid
                fields={group.fields}
                values={area.answers || {}}
                readOnly={readOnly}
                onPatch={(patch) => onAreaAnswersPatch(step.roomType, patch)}
              />
            </SubsectionCard>
          ))}
        </div>
      </div>
    );
  }

  if (step.kind === 'balcony_list') {
    const groups = getResolvedFieldsForRoomTemplate(
      step.roomTemplate,
      template
    );
    const indexes = step.balconyIndexes || [];
    return (
      <div className="flex flex-col gap-6">
        <SectionHeader
          letter={step.letter}
          title={step.title}
          hint="Set the balcony count first; instances appear after expand/autosave."
        />
        <ProgramFieldsBlock
          fields={step.programFields}
          program={program}
          onProgramPatch={onProgramPatch}
          readOnly={readOnly}
        />
        {indexes.length === 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4">
            <p className={BODY_TEXT_CLASS}>
              No balconies yet — enter balcony count above and wait for save.
            </p>
          </div>
        ) : (
          indexes.map((index) => {
            const room =
              (scopeDoc?.rooms || []).find(
                (r) =>
                  r.room_type === 'balcony' && Number(r.index) === Number(index)
              ) || null;
            const instanceId = room?.instance_id || room?.instanceId;
            return (
              <div key={`balcony-${index}`} className="flex flex-col gap-4">
                <h4 className={`${SUBSECTION_TITLE_CLASS} mb-0`}>
                  {`Balcony ${index}`}
                </h4>
                <SubsectionCard title="Label">
                  <ScopeField
                    field={{ id: 'label', label: 'Balcony label', type: 'text' }}
                    value={room?.label || ''}
                    readOnly={readOnly || !instanceId}
                    onChange={(next) => onRoomLabelChange(instanceId, next)}
                  />
                </SubsectionCard>
                {groups.map((group) => (
                  <SubsectionCard
                    key={`${index}-${group.id}`}
                    title={group.title}
                  >
                    <FieldsGrid
                      fields={group.fields}
                      values={room?.answers || {}}
                      readOnly={readOnly || !instanceId}
                      onPatch={(patch) => onRoomAnswersPatch(instanceId, patch)}
                    />
                  </SubsectionCard>
                ))}
              </div>
            );
          })
        )}
      </div>
    );
  }

  if (step.kind === 'room') {
    const room =
      (scopeDoc?.rooms || []).find(
        (r) =>
          r.room_type === step.roomType && Number(r.index) === Number(step.index)
      ) || null;
    const groups = getResolvedFieldsForRoomTemplate(
      step.roomTemplate,
      template
    );
    const instanceId = room?.instance_id || room?.instanceId;
    return (
      <div className="flex flex-col gap-6">
        <SectionHeader
          letter={step.letter}
          title={step.title}
          hint={
            !instanceId
              ? 'Room instance not expanded yet — set counts in section A and wait for expand/autosave.'
              : undefined
          }
        />
        <ProgramFieldsBlock
          fields={step.programFields}
          program={program}
          onProgramPatch={onProgramPatch}
          readOnly={readOnly}
        />
        <SubsectionCard title="Label">
          <ScopeField
            field={{ id: 'label', label: 'Room label', type: 'text' }}
            value={room?.label || ''}
            readOnly={readOnly || !instanceId}
            onChange={(next) => onRoomLabelChange(instanceId, next)}
          />
        </SubsectionCard>
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <SubsectionCard key={group.id} title={group.title}>
              <FieldsGrid
                fields={group.fields}
                values={room?.answers || {}}
                readOnly={readOnly || !instanceId}
                onPatch={(patch) => onRoomAnswersPatch(instanceId, patch)}
              />
            </SubsectionCard>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

export default TemplateScopeForm;
